package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.web.analytics.Analytics
import com.steamstreet.vegasful.web.analytics.AnalyticsEvent
import com.steamstreet.vegasful.web.analytics.AnalyticsParam
import com.steamstreet.vegasful.web.analytics.pageTypeOf
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.dom.addClass
import kotlinx.dom.hasClass
import kotlinx.dom.removeClass
import org.w3c.dom.Element
import org.w3c.dom.asList
import kotlin.js.Date
import kotlin.math.roundToInt

/**
 * How many days a chosen range covers, inclusive of both ends.
 *
 * The dates are the `YYYY-MM-DD` strings the calendar markup carries, which `Date.parse` reads as
 * UTC midnight — so the subtraction is over whole days and no timezone enters into it. [SimpleDate]
 * cannot answer this: its `plusDay` treats every month as having 31 days, which is harmless for
 * highlighting a range one cell at a time and wrong for counting one.
 */
private fun rangeDays(start: String, end: String): Int? {
    val from = Date.parse(start)
    val to = Date.parse(end)
    if (from.isNaN() || to.isNaN()) return null
    return ((to - from) / MILLISECONDS_PER_DAY).roundToInt() + 1
}

private const val MILLISECONDS_PER_DAY = 86_400_000.0

/**
 * A really very simple date class for the calendar the mostly doesn't
 * try to do the right thing.
 */
class SimpleDate(val year: Int,
                 val month: Int,
                 val date: Int) : Comparable<SimpleDate> {
    fun plusDay(): SimpleDate {
        var newYear = year
        var newMonth = month
        var newDate = date + 1
        if (date >= 31) {
            newDate = 1
            newMonth++
        }
        if (newMonth > 12) {
            newMonth = 1
            newYear++
        }
        return SimpleDate(newYear, newMonth, newDate)
    }

    override fun toString(): String {
        return "${year}-${month.pad(2)}-${date.pad(2)}"
    }

    override fun compareTo(other: SimpleDate): Int {
        val yearDiff = year - other.year
        return if (yearDiff == 0) {
            val monthDiff = month - other.month
            if (monthDiff == 0) {
                date - other.date
            } else {
                monthDiff
            }
        } else {
            yearDiff
        }
    }

    override fun equals(other: Any?): Boolean {
        return (other is SimpleDate &&
                other.year == year && other.month == month && other.date == date)
    }

    override fun hashCode(): Int {
        return toString().hashCode()
    }
}

fun SimpleDate(str: String): SimpleDate {
    val bits = str.split("-")
    return SimpleDate(bits[0].toInt(), bits[1].toInt(), bits[2].toInt())
}

class SimpleYearMonth(val year: Int, val month: Int) {
    fun plusMonth(): SimpleYearMonth {
        var newYear = year
        var newMonth = month + 1
        if (newMonth > 12) {
            newMonth = 1
            newYear++
        }
        return SimpleYearMonth(newYear, newMonth)
    }

    fun minusMonth(): SimpleYearMonth {
        var newYear = year
        var newMonth = month - 1
        if (newMonth == 0) {
            newMonth = 12
            newYear--
        }
        return SimpleYearMonth(newYear, newMonth)
    }

    override fun toString(): String = "${year}-${month.pad(2)}"
}

fun SimpleYearMonth(str: CharSequence): SimpleYearMonth {
    val bits = str.split("-")
    return SimpleYearMonth(bits[0].toInt(), bits[1].toInt())
}

fun Int.pad(size: Int): String {
    var s = "$this"
    while (s.length < size) {
        s = "0$s"
    }
    return s
}

fun calendarSelector(element: Element) {
    var selectedDate: String? = null

    element.querySelector(".calendar-trigger")?.addEventListener("click", {
        it.preventDefault()
        it.stopPropagation()
        if (element.hasClass("calendar-selection")) {
            element.removeClass("calendar-selection")
        } else {
            element.addClass("calendar-selection")
        }
    })


    /**
     * Clear the date selection by navigating without the date range.
     */
    fun clearDateSelection() {
        val origin = document.location?.origin
        val path = document.location?.pathname
        val url = origin + path
        document.location?.href = url
    }

    /**
     * Handle clicks to the clear date range.
     */
    element.querySelector(".calendar-clear-range")?.addEventListener("click", {
        it.preventDefault()
        it.stopPropagation()

        // Clearing is reported as a `date_selected` of no days rather than as its own event, so
        // that "chose a range" and "went back to everything" are two values of one measure.
        Analytics.track(
            AnalyticsEvent.DATE_SELECTED,
            AnalyticsParam.RANGE_DAYS to 0,
            AnalyticsParam.PAGE_TYPE to pageTypeOf(window.location.pathname)
        )
        clearDateSelection()
    })


    fun selectDate(date: String) {
        if (selectedDate != null) {
            val start: String
            val end: String
            if (selectedDate!! < date) {
                start = selectedDate!!
                end = date
            } else {
                start = date
                end = selectedDate!!
            }

            // show all of the dates in this range as selected for visual feedback while we load
            var simpleDate = SimpleDate(start)
            val endSimpleDate = SimpleDate(end)

            while (simpleDate <= endSimpleDate) {
                element.querySelector("[data-calendar-date='${simpleDate}']")?.addClass("calendar-selected")
                simpleDate = simpleDate.plusDay()
            }

            Analytics.track(
                AnalyticsEvent.DATE_SELECTED,
                AnalyticsParam.RANGE_DAYS to rangeDays(start, end),
                AnalyticsParam.PAGE_TYPE to pageTypeOf(window.location.pathname)
            )

            val origin = document.location?.origin
            val path = document.location?.pathname
            val url = origin + path + "?date=${start}&endDate=${end}"
            document.location?.href = url
        } else {
            selectedDate = date
            element.querySelector("[data-calendar-date='${selectedDate}']")?.addClass("calendar-selected")
        }
    }

    element.querySelectorAll("[data-calendar-date]").asList()
            .mapNotNull { it as? Element }
            .forEach { calendarNode ->
                calendarNode.addEventListener("click", {
                    val date = calendarNode.getAttribute("data-calendar-date")
                    if (date != null) {
                        selectDate(date)
                    }
                })
            }

    element.querySelector(".calendar-main")?.let {
//        val min = it.getAttribute("data-calendar-min-month")
//        val max = it.getAttribute("data-calendar-max-month")

        val visibleOne = it.querySelector(".calendar-visible")
        val attribute = visibleOne?.attributes?.asList()?.find { it.name == "data-calendar-month" }?.value
        var selectedMonth = attribute?.let {
            SimpleYearMonth(it)
        }

        fun updateSelection(month: SimpleYearMonth?) {
            if (selectedMonth != null) {
                element.querySelector("[data-calendar-month='${selectedMonth}']")?.removeClass("calendar-visible")
            }
            selectedMonth = month
            if (selectedMonth != null) {
                element.querySelector("[data-calendar-month='${selectedMonth}']")?.addClass("calendar-visible")
            }
        }

        element.querySelectorAll("[data-calendar-nav]").asList().mapNotNull { it as? Element }.forEach {
            val direction = it.getAttribute("data-calendar-nav")
            it.addEventListener("click", { clickEvent ->
                when (direction) {
                    "right" -> updateSelection(selectedMonth?.plusMonth())
                    "left" -> updateSelection(selectedMonth?.minusMonth())
                }
                clickEvent.preventDefault()
            })
        }
    }
}
